// src/api/tenants.js
// Fetches tenant list from the backend for TenantContext bootstrapping.
import api from "./httpClient";
import { API_PATHS } from "./apiPaths";

/**
 * Load all tenants from the backend.
 * Returns an array; empty array on failure so callers can fall back to localStorage.
 * @returns {Promise<Array>}
 */
export const getAllTenants = async () => {
  try {
    const resp = await api.get(API_PATHS.superAdmin.tenants());
    const data = resp?.data?.data ?? resp?.data ?? resp ?? [];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

/**
 * Fetch a single tenant by id (includes admins list if backend supports it).
 * @param {string} tenantId
 * @returns {Promise<object|null>}
 */
export const getTenantById = async (tenantId) => {
  try {
    const resp = await api.get(API_PATHS.superAdmin.tenantById(tenantId));
    return resp?.data?.data ?? resp?.data ?? null;
  } catch {
    return null;
  }
};

export const createTenant = async (tenantPayload) => {
  const resp = await api.post(API_PATHS.superAdmin.tenants(), tenantPayload);
  return resp?.data?.data ?? resp?.data ?? resp;
};

export const updateTenant = async (tenantId, tenantPayload) => {
  const resp = await api.put(API_PATHS.superAdmin.tenantById(tenantId), tenantPayload);
  return resp?.data?.data ?? resp?.data ?? resp;
};

export const deleteTenant = async (tenantId) => {
  const resp = await api.delete(API_PATHS.superAdmin.tenantById(tenantId));
  return resp?.data?.data ?? resp?.data ?? resp;
};

/**
 * Fetch the active subscription status.
 * Backend uses auth token to resolve the tenant – tenantId param is kept for
 * API compatibility but is not sent in the request.
 * Returns raw shape: { plan, expired, expiryDate, active }
 * @returns {Promise<object|null>}
 */
export const getTenantSubscription = async (_tenantId) => {
  const resp = await api.get(API_PATHS.subscription.status);
  return resp?.data?.data ?? resp?.data ?? null;
};

export const getTenantFeatures = async () => {
  const resp = await api.get(API_PATHS.tenant.moduleFeatures());
  return resp?.data?.data ?? resp?.data ?? {};
};

/**
 * Public tenant resolution by current request hostname (no authentication required).
 * Makes a same-origin GET request to /api/public/tenant/resolve.
 * Do not send a subdomain, tenant ID, subscription status, or expiry date.
 * The backend uses the request hostname to resolve the tenant and decide whether branding is available.
 * @returns {Promise<object|null>} data object containing { brandingEnabled, tenantId, schoolName, subdomain, logoUrl, ... } or null
 */
export const resolvePublicTenant = async () => {
  try {
    const resp = await api.get(API_PATHS.public.tenantResolve(), {
      baseURL: "",
      skipAuth: true,
      withCredentials: false,
    });
    const envelope = resp?.data ?? resp;
    const data = envelope?.data !== undefined ? envelope.data : envelope;
    return data;
  } catch (error) {
    // Fail closed/gracefully
    return null;
  }
};

/**
 * Public tenant resolution by subdomain (no authentication required).
 * GET /api/public/tenant/subdomain/resolve?subdomain={subdomain}
 * @param {string} subdomain
 * @returns {Promise<object|null>}
 */
export const resolveTenantBySubdomain = async (subdomain) => {
  if (!subdomain) return null;
  try {
    const resp = await api.get(API_PATHS.public.tenantSubdomainResolve(subdomain));
    const data = resp?.data?.data ?? resp?.data ?? null;
    return data;
  } catch (error) {
    // Fail closed/gracefully so caller can fall back to local/cached branding
    return null;
  }
};

/**
 * Fetch tenant school logo blob securely from /api/admin/school/logo or custom path.
 * Returns an object URL for display, or null if not found.
 * @param {string} logoPath
 * @returns {Promise<string|null>}
 */
export const fetchSchoolLogoBlob = async (logoPath = API_PATHS.admin.schoolLogo()) => {
  try {
    const response = await api.get(logoPath, {
      responseType: "blob",
      withCredentials: true,
    });
    if (response?.data && (typeof Blob !== "undefined" && response.data instanceof Blob) && response.data.size > 0) {
      if (typeof window !== "undefined" && window.URL && typeof window.URL.createObjectURL === "function") {
        return window.URL.createObjectURL(response.data);
      }
      if (typeof URL !== "undefined" && typeof URL.createObjectURL === "function") {
        return URL.createObjectURL(response.data);
      }
    }
    return null;
  } catch {
    return null;
  }
};
